import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Drop, Heartbeat, MonitorPlay, TestTube, Syringe, Warning, Info,
} from "@phosphor-icons/react";
import LungsIcon from "@/components/icons/LungsIcon";

// Map catalog color -> Tailwind class combo (must include full class strings for JIT)
const TONE = {
  red:    { critical: "bg-red-100 text-red-900 border-red-300 ring-red-200",         soft: "bg-red-50 text-red-800 border-red-200" },
  orange: { critical: "bg-orange-100 text-orange-900 border-orange-300",             soft: "bg-orange-50 text-orange-800 border-orange-200" },
  violet: { critical: "bg-violet-100 text-violet-900 border-violet-300",             soft: "bg-violet-50 text-violet-800 border-violet-200" },
  green:  { critical: "bg-emerald-100 text-emerald-900 border-emerald-300",          soft: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  blue:   { critical: "bg-sky-100 text-sky-900 border-sky-300",                      soft: "bg-sky-50 text-sky-800 border-sky-200" },
  pink:   { critical: "bg-pink-100 text-pink-900 border-pink-300",                   soft: "bg-pink-50 text-pink-800 border-pink-200" },
  yellow: { critical: "bg-amber-100 text-amber-900 border-amber-300",                soft: "bg-amber-50 text-amber-800 border-amber-200" },
  cyan:   { critical: "bg-cyan-100 text-cyan-900 border-cyan-300",                   soft: "bg-cyan-50 text-cyan-800 border-cyan-200" },
};

const ICON = {
  Lungs: LungsIcon, Drop, Heartbeat, MonitorPlay, TestTube, SyringeIcon: Syringe,
};

function ExamBadge({ meta, urgency, checked, onToggle, disabled }) {
  const Icon = ICON[meta.icon] || TestTube;
  const critical = urgency === "obbligatorio";
  const tone = (TONE[meta.color] || TONE.blue)[critical ? "critical" : "soft"];
  const done = !!checked;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      data-testid={`exam-badge-${meta.code}`}
      className={`group relative flex items-center gap-2 px-3 py-2 rounded-md border transition-all
        ${tone}
        ${critical ? "shadow-sm border-2" : "border"}
        ${done ? "opacity-50 line-through" : ""}
        hover:scale-[1.02] active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-1`}
      title={critical ? "Obbligatorio" : "Suggerito"}
    >
      {critical && !done && (
        <span className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500 text-white items-center justify-center">
            <Warning size={9} weight="fill" />
          </span>
        </span>
      )}
      <Icon size={18} weight={critical ? "fill" : "regular"} />
      <span className="flex flex-col items-start leading-tight">
        <span className="text-sm font-semibold">{meta.short}</span>
        <span className="text-[9px] uppercase tracking-widest opacity-70">
          {critical ? "Obbligatorio" : "Suggerito"}
        </span>
      </span>
    </button>
  );
}

export default function RequiredExamsAlert({ protocol }) {
  const [catalog, setCatalog] = useState([]);
  const [checkedCodes, setCheckedCodes] = useState({});
  const protoId = protocol?.protocol_id;
  const exams = protocol?.required_exams || [];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/exam_catalog");
        setCatalog(data);
      } catch (err) {
        console.warn("[required-exams] catalog load failed", err);
      }
    })();
  }, []);

  // Reset "already done" locally when the selected protocol changes
  useEffect(() => { setCheckedCodes({}); }, [protoId]);

  const catalogByCode = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { m[c.code] = c; });
    return m;
  }, [catalog]);

  const criticalOpen = exams.filter(
    (e) => e.urgency === "obbligatorio" && !checkedCodes[e.code]
  ).length;

  if (!protocol || exams.length === 0) return null;

  return (
    <div
      data-testid="required-exams-alert"
      className={`rounded-md p-4 border-2 ${
        criticalOpen > 0 ? "bg-red-50/50 border-red-300" : "bg-slate-50 border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {criticalOpen > 0 ? (
            <Warning size={18} weight="fill" className="text-red-600 animate-pulse" />
          ) : (
            <Info size={18} weight="fill" className="text-slate-500" />
          )}
          <div>
            <div className="font-heading text-sm font-semibold text-slate-900">
              Esami richiesti dal protocollo
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              {criticalOpen > 0
                ? `${criticalOpen} obbligatorio${criticalOpen > 1 ? "" : ""} da eseguire`
                : "Tutti gli esami critici gestiti"}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-slate-400" data-testid="exams-legend">
          Clicca per marcare come eseguito
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {exams.map((e) => {
          const meta = catalogByCode[e.code] || { code: e.code, label: e.code, short: e.code, color: "blue", icon: "TestTube" };
          return (
            <ExamBadge
              key={e.code}
              meta={meta}
              urgency={e.urgency}
              checked={checkedCodes[e.code]}
              onToggle={() => setCheckedCodes((cur) => ({ ...cur, [e.code]: !cur[e.code] }))}
            />
          );
        })}
      </div>
    </div>
  );
}
